<script lang="ts" setup>
import { useTemplateRef } from 'vue';
import { useFeatureToggles } from '~/composables/useFeatureToggles';

const emits = defineEmits<{
  createdCustomer: [customerId: number]
}>();

const apiFetch = getAppApiFetch();
const { t } = useEkI18n();
const { openToast, openConfirmAlert } = useUserMessages();
const { utilizeCVR, utilizeNemhandel, utilizeAddressSearch } = useFeatureToggles();
const config = useRuntimeConfig();
const defaultCountryCode = config.public.defaultCountryCode;
const defaultCurrencyId = config.public.defaultCurrencyId;

defineExpose({ open });

const emptyModel: CreateDebtor = {
  CVR: '',
  EAN: '',
  Name: '',
  Phone: '',
  Email: '',
  SearchName: '',
  Reference: null,
  InvoiceTypeId: '',
  PriceGroupId: null,
  ProjectGroupId: null,
  CurrencyId: defaultCurrencyId,
  IsLocked: false,
  AssociationAddress: {
    Address: '',
    Address2: '',
    ZipCode: '',
    City: '',
    CountryCode: defaultCountryCode
  },
  DimensionCarrierId: null,
  DimensionDepartmentId: null,
  DimensionPurposeId: null
};

const createModel = ref<CreateDebtor>({ ...emptyModel });

const addressSelectModal = useTemplateRef('addressSelectModal');
const vatModal = useTemplateRef('vatModal');
const dimensionDepartmentSelectModal = useTemplateRef('dimensionDepartmentSelectModal');
const dimensionCarrierSelectModal = useTemplateRef('dimensionCarrierSelectModal');
const dimensionPurposeSelectModal = useTemplateRef('dimensionPurposeSelectModal');
const projectGroupSelectModal = useTemplateRef('projectGroupSelectModal');
const priceGroupSelectModal = useTemplateRef('priceGroupSelectModal');
const currencySelectModal = useTemplateRef('currencySelectModal');
const invoiceTypeSelectModal = useTemplateRef('invoiceTypeSelectModal');

const isOpen = ref<boolean>(false);
const selectedAddress = ref<AddressSearch>();
const selectedDepartment = ref<DimensionDepartment>();
const selectedCarrier = ref<DimensionCarrier>();
const selectedPurpose = ref<DimensionPurpose>();
const selectedProjectGroup = ref<ProjectGroup>();
const selectedPriceGroup = ref<PriceGroup>();
const selectedCurrency = ref<Currency>();
const selectedInvoiceType = ref<InvoiceType>();

const addressTouched = ref<boolean>(false);
const zipCodeTouched = ref<boolean>(false);
const cityTouched = ref<boolean>(false);
const projectGroupTouched = ref<boolean>(false);
const priceGroupTouched = ref<boolean>(false);
const currencyTouched = ref<boolean>(false);
const dimensionCarrierTouched = ref<boolean>(false);
const dimensionDepartmentTouched = ref<boolean>(false);
const dimensionPurposeTouched = ref<boolean>(false);
const invoiceTypeTouched = ref<boolean>(false);
const eanTouched = ref<boolean>(false);

const visibleProjectGroups = computed(() => {
  return debtorInfo.value?.ProjectGroup?.filter(pg => pg.IsShownUnderCreateProjectInApp) ?? [];
});

function vatErrors(vat: string): string[] {
  if (vat.length > 20) {
    return [t('input.vatFormat')];
  }

  return [];
}

function phoneErrors(phone: string): string[] {
  const cleanNumber = phone.replace(/\D/g, ''); // Remove non-numeric characters
  const length = cleanNumber.length;

  if (length >= 7 && length <= 15) {
    return [];
  } else {
    return [t('input.phoneFormat')];
  }
}

function emailErrors(email: string): string[] {
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return [];
  } else {
    return [t('input.emailFormat')];
  }
}

const referenceIsValid = ref<boolean>(false);
const nameIsValid = ref<boolean>(false);
const phoneIsValid = ref<boolean>(false);
const emailIsValid = ref<boolean>(false);

const { data: debtorInfoResponse, status: debtorInfoStatus, execute: fetchDebtorInfo } = useApiFetch<CreateCustomerSelectionInfo>(versionedUrl('Debtor/GetSelectiveInfoForCreateDebtor'), { immediate: false });
const { status: cvrInfoStatus } = useApiFetch<CvrBasicData>(versionedUrl('Debtor/GetInfoFromCvr'), {
  immediate: false, 
  watch: false,
  query: { cvr: '' }
});

async function fetchCVRInfo(cvrNumber?: string) {
  const cvr = cvrNumber || createModel.value.CVR;
  
  if (!cvr || cvr.length !== 8) {
    return;
  }
    
  return await useApiFetch<CvrBasicData>(versionedUrl('Debtor/GetInfoFromCvr'), {
    query: { cvr }, 
    immediate: true
  });
}

const debtorInfo = computed(() => debtorInfoResponse.value?.Data);
const debtorInfoPending = computed(() => debtorInfoStatus.value === 'pending');
const availableInvoiceTypes = computed(() =>
  (debtorInfo.value?.InvoiceType ?? []).filter(i => utilizeNemhandel || i.Name !== 'Nemhandel')
);
const addressIsValid = computed(() => {
  if (utilizeAddressSearch) {
    return selectedAddress.value !== undefined;
  }

  return createModel.value.AssociationAddress.Address.trim() !== '';
});
const zipCodeIsValid = computed(() => createModel.value.AssociationAddress.ZipCode.trim() !== '');
const cityIsValid = computed(() => createModel.value.AssociationAddress.City.trim() !== '');
const eanIsValid = computed(() => {
  if (createModel.value.InvoiceTypeId === 'Nemhandel') {
    return createModel.value.EAN.length === 13 && /^\d+$/.test(createModel.value.EAN);
  }

  return true;
});
const invoiceTypeIsValid = computed(() => debtorInfo.value?.InvoiceType.some(t => t.Name === createModel.value.InvoiceTypeId) ?? false);

if (utilizeCVR) {
  watch(() => createModel.value.CVR, async (cvr) => {
    if (cvr.length !== 8 || isNaN(Number(cvr))) {
      return;
    }

    try {
      const result = await fetchCVRInfo(cvr);

      const cvrInfo = result?.data?.value?.Data;

      if (cvrInfo !== undefined && isAllValuesNull(cvrInfo) === false && cvrInfo.ZipCode !== 0) {
        vatModal.value?.openModal(toCvrInfo(cvrInfo, cvr));
      } else {
        openToast(t('customer.noCompanyFound', [cvr]), ToastType.INFO);
      }
    } catch {
      openToast(t('alert.requestFailed'), ToastType.ERROR);
    }
  });
}

const requiredInputs = reactive([
  { valid: nameIsValid, name: 'customer.customerName' },
  { valid: referenceIsValid, name: 'customer.customerNumber' },
  { valid: addressIsValid, name: 'customer.address' },
  ...(!utilizeAddressSearch ? [
    { valid: zipCodeIsValid, name: 'customer.zipCode' },
    { valid: cityIsValid, name: 'customer.city' }
  ] : []),
  { valid: invoiceTypeIsValid, name: 'customer.invoiceType' },
  { valid: eanIsValid, name: 'customer.ean' }
]);

const customerIsValid = computed(() => toValue(requiredInputs).every(v => v.valid));

function getInputClass(isTouched: boolean, isValid: boolean) {
  return [
    isTouched && isValid === false ? 'input-invalid' : 'input-valid',
    { 'ion-invalid': isValid === false, 'ion-touched': isTouched }
  ];
}

async function createCustomer() {
  if (customerIsValid.value === false) {
    const failedInputs = requiredInputs
      .filter(i => i.valid === false)
      .map(i => t(i.name))
      .join(', ');
    openToast(t('worksheet.alerts.missingInputs') + ' ' + failedInputs, ToastType.INFO);

    return;
  }

  try {
    const response = await apiFetch<ResponseDTO<SavedDebtorsResponseDTO>>(versionedUrl('Debtor/CreateDebtor'), {
      method: 'POST',
      body: createModel.value
    });

    if (response.Data.HasFailedDebtors === true) {
      return;
    } else {
      openToast(t('customer.wasCreated'), ToastType.SUCCESS);
      reset();
      isOpen.value = false;
      emits('createdCustomer', response.Data.SavedDebtors[0].ID);
    }

  } catch {
    return;
  }
}

async function open() {
  isOpen.value = true;
  await fetchDebtorInfo();
}

async function handleClose() {
  if (isCreateModelAltered() === false) {
    isOpen.value = false;

    return;
  }

  const confirmed = await openConfirmAlert(t('buttons.confirm'), t('alert.cancelCreateCustomer'), t('buttons.yes'), t('buttons.no'));

  if (confirmed === true) {
    reset();
    isOpen.value = false;
  }
}

function willPresent() {
  canDismissOverride = false;
}

function didDismiss() {
  canDismissOverride = true; 
  isOpen.value = false;
}

let canDismissOverride = false;

async function canDismiss() {
  if (canDismissOverride) {
    return true;
  }

  if (isCreateModelAltered() === false) {
    return true;
  }

  const confirmed = await openConfirmAlert(t('buttons.confirm'), t('alert.cancelCreateCustomer'), t('buttons.yes'), t('buttons.no'));

  if (confirmed === true) {
    reset();

    return true;
  } else {
    isOpen.value = true;

    return false;
  }
}

function isCreateModelAltered(): boolean {
  return JSON.stringify(createModel.value) !== JSON.stringify(emptyModel);
}

function reset() {
  touchInputFieldsDisable();
  selectionValuesUndefined();
  createModel.value = { ...emptyModel };
}

function selectionValuesUndefined() {
  selectedCurrency.value = undefined;
  selectedCarrier.value = undefined;
  selectedDepartment.value = undefined;
  selectedPurpose.value = undefined;
  selectedPriceGroup.value = undefined;
  selectedProjectGroup.value = undefined;
  selectedInvoiceType.value = undefined;
  selectedAddress.value = undefined;
}

function touchInputFieldsDisable() {
  addressTouched.value = false;
  zipCodeTouched.value = false;
  cityTouched.value = false;
  projectGroupTouched.value = false;
  priceGroupTouched.value = false;
  currencyTouched.value = false;
  dimensionCarrierTouched.value = false;
  dimensionDepartmentTouched.value = false;
  dimensionPurposeTouched.value = false;
  invoiceTypeTouched.value = false;
  eanTouched.value = false;
}

function mapResultToCvrInfo(result: CvrInfo) {
  const { CVR, Name, Phone, Email, Address, City, ZipCode } = result;

  createModel.value.CVR = CVR ?? '';
  createModel.value.Name = Name ?? '';
  createModel.value.Phone = Phone ?? '';
  createModel.value.Email = Email ?? '';
  createModel.value.AssociationAddress.Address = Address ?? '';
  createModel.value.AssociationAddress.City = City ?? '';
  createModel.value.AssociationAddress.ZipCode = ZipCode ?? '';

  selectedAddress.value = {
    StreetName: Address ?? '',
    Unit: '',
    City: City ?? '',
    Zip: ZipCode ?? ''
  };
}

function isAllValuesNull(data: CvrBasicData): boolean {
  return Object.values(data).every(value => value === null);
}

function toCvrInfo(result: CvrBasicData, query: string): CvrInfo {
  return {
    ...result,
    CVR: query,
    ZipCode: result.ZipCode?.toString() ?? ''
  };
}

</script>

<template>
  <IonModal
    id="modal"
    :is-open="isOpen"
    :can-dismiss="canDismiss"
    :close-override="handleClose"
    @did-dismiss="didDismiss"
    @will-present="willPresent"
  >
    <div class="web-modal-wrapper">
      <IonHeader class="ion-no-border">
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton @click="() => { isOpen = false; }">
              <IonIcon
                slot="icon-only"
                class="fa-light fa-xmark"
              />
            </IonButton>
          </IonButtons>
          <IonTitle>
            {{ t('customer.createCustomer') }}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <div class="scrollable-content">
        <IonProgressBar
          v-show="(utilizeCVR && cvrInfoStatus === 'pending') || debtorInfoPending"
          type="indeterminate"
        />

        <IonGrid>
          <IonRow class="ion-justify-content-between ion-align-items-center">
            <IonCol
              size="12"
              size-md="6"
            >
              <InputSection
                icon="fa-light fa-user"
                :title="t('titles.general')"
              >
                <IonRow>
                  <IonCol
                    v-if="utilizeCVR"
                    size="12"
                    size-md="6"
                  >
                    <InputWrapper
                      clearable
                      @clear="() => { createModel.CVR = ''}"
                    >
                      <InputGenericValid
                        v-model="createModel.CVR"
                        :validate="vatErrors"
                        :label="t('customer.vat')"
                      />
                    </InputWrapper>
                  </IonCol>
                  <IonCol
                    size="12"
                    size-md="6"
                  >
                    <InputWrapper
                      clearable
                      @clear="() => { createModel.Reference = null }"
                    >
                      <InputGenericValid
                        v-model="createModel.Reference"
                        v-model:is-valid="referenceIsValid"
                        type="number"
                        :label="t('customer.customerNumber')"
                        :required="true"
                      />
                    </InputWrapper>
                  </IonCol>
                </IonRow>
                <InputWrapper
                  clearable
                  @clear="() => { createModel.Name = '' }"
                >
                  <InputGenericValid
                    v-model="createModel.Name"
                    v-model:is-valid="nameIsValid"
                    :label="t('customer.customerName')"
                    :required="true"
                  />
                </InputWrapper>
                <InputWrapper
                  clearable
                  @clear="() => { createModel.SearchName = '' }"
                >
                  <InputGenericValid
                    v-model="createModel.SearchName"
                    :label="t('customer.searchName')"
                  />
                </InputWrapper>
              </InputSection>

              <InputSection
                icon="fa-light fa-address-card"
                :title="t('customer.contactDetails')"
              >
                <IonRow>
                  <IonCol
                    size="12"
                    size-md="6"
                  >
                    <InputWrapper
                      clearable
                      @clear="() => { createModel.Phone = '' }"
                    >
                      <InputGenericValid
                        v-model="createModel.Phone"
                        v-model:is-valid="phoneIsValid"
                        type="tel"
                        :validate="phoneErrors"
                        :label="t('customer.telephone')"
                      />
                    </InputWrapper>
                  </IonCol>
                  <IonCol
                    size="12"
                    size-md="6"
                  >
                    <InputWrapper
                      clearable
                      @clear="() => { createModel.Email = '' }"
                    >
                      <InputGenericValid
                        v-model="createModel.Email"
                        v-model:is-valid="emailIsValid"
                        :label="t('worksheet.email')"
                        type="email"
                        :validate="emailErrors"
                      />
                    </InputWrapper>
                  </IonCol>
                </IonRow>
                <template v-if="utilizeAddressSearch">
                  <InputWrapper
                    clearable
                    @clear="() => { selectedAddress = undefined }"
                  >
                    <IonInput
                      :class="getInputClass(addressTouched, addressIsValid)"
                      :value="createFullAddress(selectedAddress?.StreetName, selectedAddress?.StreetNumber, selectedAddress?.Unit, selectedAddress?.Zip, selectedAddress?.City)"
                      :label="t('customer.address') + '*'"
                      readonly
                      helper-text=" "
                      :error-text="t('materials.createItems.errorText')"
                      label-placement="floating"
                      @click="() => addressSelectModal?.open()"
                      @ion-blur="addressTouched = true"
                    />
                  </InputWrapper>
                </template>
                <template v-else>
                  <InputWrapper
                    clearable
                    @clear="() => { createModel.AssociationAddress.Address = '' }"
                  >
                    <IonInput
                      v-model="createModel.AssociationAddress.Address"
                      :class="getInputClass(addressTouched, addressIsValid)"
                      :label="t('customer.streetAndNumber') + '*'"
                      helper-text=" "
                      :error-text="t('materials.createItems.errorText')"
                      label-placement="floating"
                      @ion-blur="addressTouched = true"
                    />
                  </InputWrapper>
                  <IonRow>
                    <IonCol
                      size="12"
                      size-md="6"
                    >
                      <InputWrapper
                        clearable
                        @clear="() => { createModel.AssociationAddress.ZipCode = '' }"
                      >
                        <IonInput
                          v-model="createModel.AssociationAddress.ZipCode"
                          :class="getInputClass(zipCodeTouched, zipCodeIsValid)"
                          :label="t('customer.zipCode') + '*'"
                          helper-text=" "
                          :error-text="t('materials.createItems.errorText')"
                          label-placement="floating"
                          @ion-blur="zipCodeTouched = true"
                        />
                      </InputWrapper>
                    </IonCol>
                    <IonCol
                      size="12"
                      size-md="6"
                    >
                      <InputWrapper
                        clearable
                        @clear="() => { createModel.AssociationAddress.City = '' }"
                      >
                        <IonInput
                          v-model="createModel.AssociationAddress.City"
                          :class="getInputClass(cityTouched, cityIsValid)"
                          :label="t('customer.city') + '*'"
                          helper-text=" "
                          :error-text="t('materials.createItems.errorText')"
                          label-placement="floating"
                          @ion-blur="cityTouched = true"
                        />
                      </InputWrapper>
                    </IonCol>
                  </IonRow>
                </template>
                <InputWrapper
                  clearable
                  @clear="() => { createModel.AssociationAddress.Address2 = '' }"   
                > 
                  <IonInput
                    v-model="createModel.AssociationAddress.Address2"
                    helper-text=" "
                    :label="t('customer.address2')"
                    type="text"
                    label-placement="floating"
                  />
                </InputWrapper>
              </InputSection>
            </IonCol>
            <IonCol
              size="12"
              size-md="6"
            >
              <InputSection
                icon="fa-light fa-fw fa-building-user"
                :title="t('ekomplet.dimensions')"
              >
                <IonRow>
                  <IonCol
                    size="12"
                    size-md="6"
                  >
                    <InputWrapper
                      :loading="debtorInfoPending"
                      clearable
                      @clear="() => { selectedDepartment = undefined; createModel.DimensionDepartmentId = null }"
                    >
                      <IonInput
                        :value="selectedDepartment?.Name"
                        :label="t('customer.department')"
                        helper-text=" "
                        readonly
                        label-placement="floating"
                        @click="dimensionDepartmentSelectModal?.open(); dimensionDepartmentTouched = true;"
                      />
                    </InputWrapper>
                  </IonCol>
                  <IonCol
                    size="12"
                    size-md="6"
                  >
                    <InputWrapper
                      :loading="debtorInfoPending"
                      clearable
                      @clear="() => { selectedCarrier = undefined; createModel.DimensionCarrierId = null; }"
                    >
                      <IonInput
                        :value="selectedCarrier?.Name"
                        :label="t('customer.carrier')"
                        helper-text=" "
                        readonly
                        label-placement="floating"
                        @click="dimensionCarrierSelectModal?.open(); dimensionCarrierTouched = true;"
                      />
                    </InputWrapper>
                  </IonCol>
                </IonRow>
                <InputWrapper
                  :loading="debtorInfoPending"
                  clearable
                  @clear="() => { selectedPurpose = undefined; createModel.DimensionPurposeId = null; }"
                >
                  <IonInput
                    :value="selectedPurpose?.Name"
                    :label="t('customer.purpose')"
                    helper-text=" "
                    readonly
                    label-placement="floating"
                    @click="dimensionPurposeSelectModal?.open(); dimensionPurposeTouched = true;"
                  />
                </InputWrapper>
              </InputSection>

              <InputSection
                icon="fa-light fa-clipboard"
                :title="t('ekomplet.project')"
              >
                <InputWrapper
                  :loading="debtorInfoPending"
                  clearable
                  @clear="() => { selectedProjectGroup = undefined; createModel.ProjectGroupId = null; }"
                >
                  <IonInput
                    :value="selectedProjectGroup?.Name"
                    helper-text=" "
                    :label="t('customer.projectGroup')"
                    readonly
                    label-placement="floating"
                    @click="projectGroupSelectModal?.open(); projectGroupTouched = true;"
                  />
                </InputWrapper>
              </InputSection>

              <InputSection
                icon="fa-light fa-coins"
                :title="t('customer.payment')"
              >
                <IonRow>
                  <IonCol
                    size="12"
                    size-md="6"
                  >
                    <InputWrapper
                      :loading="debtorInfoPending"
                      clearable
                      @clear="() => { selectedPriceGroup = undefined; createModel.PriceGroupId = null }"
                    >
                      <IonInput
                        :value="selectedPriceGroup?.Reference"
                        :label="t('customer.priceGroup')"
                        readonly
                        helper-text=" "
                        label-placement="floating"
                        @click="priceGroupSelectModal?.open(); priceGroupTouched = true;"
                      />
                    </InputWrapper>
                  </IonCol>
                  <IonCol
                    size="12"
                    size-md="6"
                  >
                    <InputWrapper
                      :loading="debtorInfoPending"
                      clearable
                      @clear="() => { selectedCurrency = undefined; createModel.CurrencyId = null }"
                    >
                      <IonInput
                        :value="selectedCurrency?.CurrencyCode"
                        :label="t('customer.currency')"
                        readonly
                        helper-text=" "
                        label-placement="floating"
                        @click="currencySelectModal?.open(); currencyTouched = true;"
                      />
                    </InputWrapper>
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol
                    size="12"
                    size-md="6"
                  >
                    <InputWrapper :loading="debtorInfoPending">
                      <IonInput
                        :value="selectedInvoiceType?.Name"
                        readonly
                        :class="getInputClass(invoiceTypeTouched, invoiceTypeIsValid)"
                        :label="t('customer.invoiceType') + '*'"
                        :error-text="t('materials.createItems.errorText')"
                        label-placement="floating"
                        @click="invoiceTypeSelectModal?.open(); invoiceTypeTouched = true;"
                      />
                    </InputWrapper>
                  </IonCol>
                  <IonCol
                    v-if="selectedInvoiceType?.Name === 'Nemhandel'"
                    size="12"
                    size-md="6"
                  >
                    <InputWrapper>
                      <InputGenericValid
                        v-model="createModel.EAN"
                        v-model:is-valid="eanIsValid"
                        label="EAN"
                        :required="true"
                      />
                    </InputWrapper>
                  </IonCol>
                </IonRow>
              </InputSection>
            </IonCol> 
          </IonRow>
        </IonGrid>
      </div>
    </div>
    <IonFooter class="ion-padding-bottom">
      <IonRow class="footer-buttons">
        <IonCol
          size="6"
          size-md="auto"
        >
          <IonButton
            shape="round"
            fill="outline"
            expand="block"
            @click="isOpen = false"
          >
            {{ t('buttons.cancel') }}
          </IonButton>
        </IonCol>
        <IonCol
          size="6"
          size-md="auto"
        >
          <IonButton
            shape="round"
            expand="block"
            @click="createCustomer"
          >
            {{ t('buttons.create') }}
          </IonButton>
        </IonCol>
      </IonRow>
    </IonFooter>
  </IonModal>
  <InputGenericSingleSelect
    ref="currencySelectModal"
    :items="debtorInfo?.Currency ?? []"
    :title="t('customer.chooseCurrency')"
    :resolve-key="(a: Currency) => a.CurrencyCode"
    :searchable-fields="['CurrencyCode']"
    @select="(a: Currency) => { selectedCurrency = a; createModel.CurrencyId = a.CurrencyCode; }"
  >
    <template #item="{item}">
      {{ item.CurrencyCode }}
    </template>
  </InputGenericSingleSelect>
  <InputGenericSingleSelect
    ref="dimensionCarrierSelectModal"
    :items="debtorInfo?.DimensionCarrier ?? []"
    :title="t('customer.chooseCarrier')"
    :resolve-key="(a: DimensionCarrier) => a.ID"
    :searchable-fields="['Name']"
    @select="(a: DimensionCarrier) => { selectedCarrier = a; createModel.DimensionCarrierId = a.ID; }"
  >
    <template #item="{item}">
      {{ item.Name }}
    </template>
  </InputGenericSingleSelect>
  <InputGenericSingleSelect
    ref="dimensionDepartmentSelectModal"
    :items="debtorInfo?.DimensionDepartment ?? []"
    :title="t('customer.chooseDepartment')"
    :resolve-key="(a: DimensionDepartment) => a.ID"
    :searchable-fields="['Name']"
    @select="(a: DimensionDepartment) => { selectedDepartment = a; createModel.DimensionDepartmentId = a.ID; }"
  >
    <template #item="{item}">
      {{ item.Name }}
    </template>
  </InputGenericSingleSelect>
  <InputGenericSingleSelect
    ref="dimensionPurposeSelectModal"
    :items="debtorInfo?.DimensionPurpose ?? []"
    :title="t('customer.choosePurpose')"
    :resolve-key="(a: DimensionPurpose) => a.ID"
    :searchable-fields="['Name']"
    @select="(a: DimensionPurpose) => { selectedPurpose = a; createModel.DimensionPurposeId = a.ID; }"
  >
    <template #item="{item}">
      {{ item.Name }}
    </template>
  </InputGenericSingleSelect>
  <InputGenericSingleSelect
    ref="priceGroupSelectModal"
    :items="debtorInfo?.PriceGroup ?? []"
    :title="t('customer.choosePriceGroup')"
    :resolve-key="(a: PriceGroup) => a.ID"
    :searchable-fields="['Reference', 'Description']"
    @select="(a: PriceGroup) => { selectedPriceGroup = a; createModel.PriceGroupId = a.ID; }"
  >
    <template #item="{item}">
      {{ item.Reference }} - {{ item.Description }}
    </template>
  </InputGenericSingleSelect>
  <InputGenericSingleSelect
    ref="projectGroupSelectModal"
    :items="visibleProjectGroups"
    :title="t('customer.chooseProjectGroup')"
    :resolve-key="(a: ProjectGroup) => a.ID"
    :searchable-fields="['Name']"
    @select="(a: ProjectGroup) => { selectedProjectGroup = a; createModel.ProjectGroupId = a.ID; }"
  >
    <template #item="{item}">
      {{ item.Name }} - {{ item.Description }}
    </template>
  </InputGenericSingleSelect>
  <InputGenericSingleSelect
    ref="invoiceTypeSelectModal"
    :items="availableInvoiceTypes"
    :title="t('customer.chooseInvoiceType')"
    :resolve-key="(a: InvoiceType) => a.Name"
    :searchable-fields="['Name']"
    @select="(a: InvoiceType) => { selectedInvoiceType = a; createModel.InvoiceTypeId = a.Name; }"
  >
    <template #item="{item}">
      {{ item.Name }}
    </template>
  </InputGenericSingleSelect>
  <InputAddressSearch
    v-if="utilizeAddressSearch"
    ref="addressSelectModal"
    @select="(a: AddressSuggestion) => { selectedAddress = mapAddressSuggestionToAddress(a);
                                         createModel.AssociationAddress.Address = [selectedAddress.StreetName, selectedAddress.StreetNumber].filter(Boolean).join(' ');
                                         createModel.AssociationAddress.City = selectedAddress.City ?? '';
                                         createModel.AssociationAddress.ZipCode = selectedAddress.Zip ?? ''; }"
  />
  <CustomersVatModal
    v-if="utilizeCVR"
    ref="vatModal"
    @select="mapResultToCvrInfo"
  />
</template>

<style scoped>
.input-invalid {
  --border-color: var(--ion-color-danger)
}

.input-valid {
  --border-color: var(--ion-color-step-200)
}

ion-input {
  --highlight-color-focused: var(--ion-text-color);
}

ion-col ion-col {
  padding: 0px !important; 
}

.footer-buttons {
  justify-content: flex-end;
}

@media (min-width: 768px) {
  ion-modal#modal {
    --width: 90%;
  }
}
</style>
